import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  Stack,
  Button,
  MenuItem,
  TextField,
  Container,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  SelectChangeEvent
} from '@mui/material';
import { BranchStatus, getBranchById, updateBranch, type BranchDto, type BranchWorkingHour } from 'src/api/branch_api_data';
import { companyApi, type CompanyDto } from 'src/api/company.api';

import {
  WorkingHoursGrid,
  validateWorkingHours,
  normalizeWorkingHours,
  workingHoursFromSingle,
} from '../components/WorkingHoursGrid';

// Form için ayrı tip tanımı (string değerler)
interface BranchFormData {
  id?: string;
  name: string;
  address: string;
  longitude: string | number;
  latitude: string | number;
  dailyEmployeeCount?: number;
  branchOpeningTime: string;
  branchClosingTime: string;
  averageDailyAttendance?: number;
  status: BranchStatus | '';
  responsibleUsernames: string[];
  phone: string;
  companyId: string;
  companyName?: string;
}

export default function BranchEditView() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true); // FE-10: yalnız ilk veri yüklemesi (tam-sayfa spinner)
  const [submitting, setSubmitting] = useState(false); // FE-10: kaydetme — butonu disable eder, formu spinner'a çevirmez
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<BranchFormData>({
    name: '',
    address: '',
    longitude: '',
    latitude: '',
    branchOpeningTime: '',
    branchClosingTime: '',
    status: '',
    responsibleUsernames: [],
    phone: '',
    companyId: ''
  });
  const [companies, setCompanies] = useState<CompanyDto[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [workingHours, setWorkingHours] = useState<BranchWorkingHour[]>([]);
  const [workingHoursError, setWorkingHoursError] = useState<string | null>(null);

  // Şirketleri component mount'ta çek
  const fetchCompanies = useCallback(async () => {
    if (companies.length > 0) return;
    setLoadingCompanies(true);
    try {
      // Önce rolleme bazlı endpoint'i dene
      try {
        const companyList = await companyApi.getCompaniesForCurrentUser();
        setCompanies(companyList.map(c => ({ id: c.id, name: c.name } as CompanyDto)));
        console.log('Companies loaded via role-based endpoint:', companyList);
        return;
      } catch (roleBasedError) {
        console.log('Role-based endpoint failed, trying fallback:', roleBasedError);
      }

      // Fallback: getSimpleList dene
      try {
        const companyList = await companyApi.getSimpleList();
        setCompanies(companyList.map(c => ({ id: c.id, name: c.name } as CompanyDto)));
        console.log('Companies loaded via simple list:', companyList);
        return;
      } catch (simpleListError) {
        console.log('Simple list failed, trying getAll:', simpleListError);
      }

      // Fallback: getAll dene
      try {
        const companyList = await companyApi.getAll().then(res => res.content || []);
        setCompanies(companyList);
        console.log('Companies loaded via getAll:', companyList);
      } catch (getAllError) {
        console.error('Companies fetch failed:', getAllError);
        setCompanies([]);
      }
    } catch (err) {
      console.error('Unexpected error in fetchCompanies:', err);
      setCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  }, [companies.length]);

  // Fetch companies on component mount
  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!id) throw new Error('Şube ID bulunamadı');
        const branch = await getBranchById(id);
        setFormData({
          id: branch.id,
          name: branch.name || '',
          address: branch.address || '',
          longitude: branch.longitude?.toString() || '',
          latitude: branch.latitude?.toString() || '',
          dailyEmployeeCount: branch.dailyEmployeeCount ?? 0,
          branchOpeningTime: branch.branchOpeningTime || '',
          branchClosingTime: branch.branchClosingTime || '',
          averageDailyAttendance: branch.averageDailyAttendance ?? 0,
          status: branch.status ?? '',
          responsibleUsernames: branch.responsibleUsernames || [],
          phone: branch.phone || '',
          companyId: branch.companyId || '',
          companyName: branch.companyName || ''
        });
        // Backend her zaman 7 kayıt döner; yoksa eski tekil saatlerden türet.
        setWorkingHours(
          branch.workingHours && branch.workingHours.length > 0
            ? normalizeWorkingHours(branch.workingHours)
            : workingHoursFromSingle(branch.branchOpeningTime, branch.branchClosingTime)
        );
      } catch (err: any) {
        setError(err.message || 'Veriler yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value
    }));
  };

  // Select için özel fonksiyon
  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value
    }));
  };

  const handleSubmit = async () => {
    if (!id) return;
    if (submitting) return; // FE-10: çift-tıklama koruması
    setError(null);

    const normalizeCoordinate = (value: string | number | undefined): number | undefined => {
      if (value === '' || value === undefined) return undefined;
      if (typeof value === 'number') return value;
      const normalized = value.toString().replace(',', '.');
      return parseFloat(normalized);
    };

    const lat = normalizeCoordinate(formData.latitude);
    const lon = normalizeCoordinate(formData.longitude);

    if (lat === undefined || isNaN(lat) || lat < -90 || lat > 90) {
      setError('Geçersiz enlem değeri (-90 ile 90 arasında olmalıdır)');
      return;
    }
    if (lon === undefined || isNaN(lon) || lon < -180 || lon > 180) {
      setError('Geçersiz boylam değeri (-180 ile 180 arasında olmalıdır)');
      return;
    }

    if (!validateWorkingHours(workingHours)) {
      setWorkingHoursError('Açık günlerde açılış ve kapanış saati farklı olmalıdır.');
      return;
    }

    setSubmitting(true);
    try {
      // Geriye dönük uyumluluk: ilk açık günün saatlerini tekil alanlara yansıt.
      const firstOpen = workingHours.find((wh) => !wh.closed);

      // FormData'yı UpdateBranchDto'ya dönüştür
      const updateData = {
        name: formData.name,
        address: formData.address,
        longitude: lon,
        latitude: lat,
        branchOpeningTime: firstOpen?.openTime || formData.branchOpeningTime || undefined,
        branchClosingTime: firstOpen?.closeTime || formData.branchClosingTime || undefined,
        status: formData.status === '' ? undefined : formData.status,
        responsibleUsernames: formData.responsibleUsernames,
        companyId: formData.companyId || undefined,
        workingHours
      };

      await updateBranch(id, updateData);
      navigate('/dashboard/branches');
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Şube güncellenirken bir hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container><Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box></Container>
    );
  }

  return (
    <Container>
        <Box mb={5}>
          <Typography variant="h4">Şubeyi Düzenle</Typography>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Card sx={{ p: 3 }}>
          <Stack spacing={3}>
            <TextField
              label="Şube Adı"
              name="name"
              value={formData.name || ''}
              onChange={handleChange}
              fullWidth
              required
            />
            <FormControl fullWidth required error={!formData.companyId} disabled={loadingCompanies}>
              <InputLabel>Firma</InputLabel>
              <Select
                label="Firma"
                name="companyId"
                value={formData.companyId || ''}
                onChange={handleSelectChange}
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {loadingCompanies
                  ? 'Firmalar yükleniyor...'
                  : companies.length === 0 && !loadingCompanies
                  ? 'Firma seçimi gereklidir'
                  : ''}
              </FormHelperText>
            </FormControl>
            <TextField
              select
              label="Durum"
              name="status"
              value={formData.status ?? ''}
              onChange={handleChange}
              fullWidth
            >
              <MenuItem value="">Seçiniz</MenuItem>
              <MenuItem value={BranchStatus.OPEN}>Açık</MenuItem>
              <MenuItem value={BranchStatus.CLOSED}>Kapalı</MenuItem>
              <MenuItem value={BranchStatus.MAINTENANCE}>Bakımda</MenuItem>
            </TextField>
            <TextField
              label="Enlem"
              name="latitude"
              type="text"
              value={formData.latitude ?? ''}
              onChange={handleChange}
              fullWidth
              placeholder="Örn: 41.0082 veya 41,0082"
              helperText="Enlem değeri (-90 ile 90 arası)"
            />
            <TextField
              label="Boylam"
              name="longitude"
              type="text"
              value={formData.longitude ?? ''}
              onChange={handleChange}
              fullWidth
              placeholder="Örn: 28.9784 veya 28,9784"
              helperText="Boylam değeri (-180 ile 180 arası)"
            />
            <Box>
              <WorkingHoursGrid
                value={workingHours}
                onChange={(v) => {
                  setWorkingHours(v);
                  if (workingHoursError) setWorkingHoursError(null);
                }}
              />
              {workingHoursError && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  {workingHoursError}
                </Typography>
              )}
            </Box>
            <TextField
              label="Adres"
              name="address"
              value={formData.address || ''}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
            />
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => navigate('/dashboard/branches')} disabled={submitting}>İptal</Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                color="primary"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {submitting ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </Stack>
          </Stack>
        </Card>
      </Container>
  );
} 